/**
 * AuditPickerModal Component
 *
 * Modal for selecting a CS Audit to assign to a weekly assignment.
 * Fetches audits from Gobodo and resolves client names.
 * Filters to show only UNASSIGNED audits (Auditor__c is empty).
 */

import React, { useState, useMemo } from 'react';
import { X, Search, Calendar, User, Building2, FileText, Loader2, AlertCircle } from 'lucide-react';
import {
  useCSAudits,
  useAccountIdToName,
  lookupAccountDisplayName,
  useAuditorIdToName,
  lookupAuditorDisplayName,
} from '../../services/queries/cyberSweepQueries';

/**
 * Format ISO date (yyyy-MM-dd) for display as MM/DD/YYYY
 */
const formatDateForDisplay = (isoDate: string | undefined): string => {
  if (!isoDate) return '';
  try {
    const datePart = isoDate.includes('T') ? isoDate.split('T')[0] : isoDate;
    const [year, month, day] = datePart.split('-');
    if (year && month && day) {
      return `${month}/${day}/${year}`;
    }
    const date = new Date(isoDate);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });
    }
    return isoDate;
  } catch {
    return isoDate;
  }
};

export interface SelectedAuditData {
  auditId: string;
  auditName: string;
  auditStatus: string;
  auditType: string;
  clientId: string;
  clientName: string;
  dueDate: string;
  auditorId: string;
  auditorName: string;
}

interface AuditPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (data: SelectedAuditData) => void;
  /** Optional: filter to audits assigned to this auditor ID */
  auditorIdFilter?: string;
}

const AuditPickerModal: React.FC<AuditPickerModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  auditorIdFilter,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showAssigned, setShowAssigned] = useState<boolean>(false); // Default: show only unassigned

  // Fetch all CS Audits - must pass fetchAllCSAudits: true to get all audits
  const { data: csAudits = [], isLoading: auditsLoading } = useCSAudits(undefined, { fetchAllCSAudits: true });

  // Resolve client names
  const { data: accountNameMap } = useAccountIdToName(csAudits as Record<string, unknown>[]);

  // Resolve auditor names
  const { data: auditorNameMap } = useAuditorIdToName(csAudits as Record<string, unknown>[]);

  // Extract unique statuses for filter dropdown
  const statusOptions = useMemo(() => {
    const statuses = new Set<string>();
    csAudits.forEach((audit: Record<string, unknown>) => {
      const data = (audit.data as Record<string, unknown>) ?? audit;
      const status = String(
        data.Phase_Status__c ?? data.phaseStatus ?? data.Status__c ?? data.status ?? ''
      );
      if (status) statuses.add(status);
    });
    return Array.from(statuses).sort();
  }, [csAudits]);

  // Filter and search audits
  const filteredAudits = useMemo(() => {
    return csAudits.filter((audit: Record<string, unknown>) => {
      const data = (audit.data as Record<string, unknown>) ?? audit;

      // Get audit fields
      const auditName = String(audit.Name__c ?? audit.name ?? data.Name__c ?? data.name ?? audit.label ?? '');
      const auditStatus = String(data.Phase_Status__c ?? data.phaseStatus ?? data.Status__c ?? data.status ?? '');
      const auditType = String(audit.auditTypeDisplayName ?? data.Audit_Type__c ?? data.auditType ?? '');
      const clientRef = String(
        audit.Account__c ?? audit.custom__account ?? audit.ClientId__c ??
        data.Account__c ?? data.custom__account ?? data.ClientId__c ?? ''
      );
      const clientName = lookupAccountDisplayName(accountNameMap, clientRef) || clientRef;
      const auditorRef = String(data.Auditor__c ?? data.auditor ?? data.custom__auditor ?? audit.Auditor__c ?? '');

      // Filter by assignment status (only show unassigned by default)
      const isAssigned = auditorRef && auditorRef.trim() !== '';
      if (!showAssigned && isAssigned) {
        return false; // Hide already-assigned audits
      }

      // Filter out completed audits (always hide these - they don't need assignment)
      const statusLower = auditStatus.toLowerCase();
      const isCompleted = statusLower.includes('complete') || statusLower === 'done' || statusLower === 'closed';
      if (isCompleted) {
        return false; // Hide completed audits
      }

      // Status filter
      if (statusFilter !== 'all' && auditStatus !== statusFilter) {
        return false;
      }

      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          auditName.toLowerCase().includes(searchLower) ||
          clientName.toLowerCase().includes(searchLower) ||
          auditType.toLowerCase().includes(searchLower) ||
          auditStatus.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      return true;
    });
  }, [csAudits, accountNameMap, searchTerm, statusFilter, showAssigned]);

  // Count assigned vs unassigned for display
  const auditCounts = useMemo(() => {
    let assigned = 0;
    let unassigned = 0;
    let completed = 0;
    csAudits.forEach((audit: Record<string, unknown>) => {
      const data = (audit.data as Record<string, unknown>) ?? audit;
      const auditorRef = String(data.Auditor__c ?? data.auditor ?? data.custom__auditor ?? audit.Auditor__c ?? '');
      const auditStatus = String(data.Phase_Status__c ?? data.phaseStatus ?? data.Status__c ?? data.status ?? '').toLowerCase();

      // Check if completed
      const isCompleted = auditStatus.includes('complete') || auditStatus === 'done' || auditStatus === 'closed';
      if (isCompleted) {
        completed++;
      } else if (auditorRef && auditorRef.trim() !== '') {
        assigned++;
      } else {
        unassigned++;
      }
    });
    return { assigned, unassigned, completed };
  }, [csAudits]);

  // Handle selection
  const handleSelect = (audit: Record<string, unknown>) => {
    const data = (audit.data as Record<string, unknown>) ?? audit;

    const auditId = String(audit.id ?? audit.key ?? '');
    const auditName = String(audit.Name__c ?? audit.name ?? data.Name__c ?? data.name ?? audit.label ?? '');
    const auditStatus = String(data.Phase_Status__c ?? data.phaseStatus ?? data.Status__c ?? data.status ?? '');
    const auditType = String(audit.auditTypeDisplayName ?? data.Audit_Type__c ?? data.auditType ?? '');
    const clientRef = String(
      audit.Account__c ?? audit.custom__account ?? audit.ClientId__c ??
      data.Account__c ?? data.custom__account ?? data.ClientId__c ?? ''
    );
    const clientName = lookupAccountDisplayName(accountNameMap, clientRef) || clientRef;
    const dueDate = String(data.Due_Date__c ?? data.dueDate ?? data.Due_Date ?? '');
    const auditorRef = String(data.Auditor__c ?? data.auditor ?? data.custom__auditor ?? audit.Auditor__c ?? '');
    const auditorName = lookupAuditorDisplayName(auditorNameMap, auditorRef) || auditorRef;

    onSelect({
      auditId,
      auditName,
      auditStatus,
      auditType,
      clientId: clientRef,
      clientName,
      dueDate,
      auditorId: auditorRef,
      auditorName,
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Select CS Audit
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search audits, clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="all">All Statuses</option>
            {statusOptions.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>

          {/* Show Assigned Toggle */}
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={showAssigned}
              onChange={(e) => setShowAssigned(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500"
            />
            Show assigned ({auditCounts.assigned})
          </label>
        </div>

        {/* Info Banner */}
        {!showAssigned && auditCounts.unassigned === 0 && auditCounts.assigned > 0 && (
          <div className="px-6 py-2 bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800">
            <div className="flex items-center gap-2 text-sm text-yellow-800 dark:text-yellow-200">
              <AlertCircle className="h-4 w-4" />
              <span>All {auditCounts.assigned} audits are already assigned. Check "Show assigned" to see them.</span>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {auditsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 text-purple-600 animate-spin" />
              <span className="ml-2 text-gray-500 dark:text-gray-400">Loading audits...</span>
            </div>
          ) : filteredAudits.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
              <p className="font-medium">No audits found</p>
              <p className="text-sm mt-1">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredAudits.map((audit: Record<string, unknown>) => {
                const data = (audit.data as Record<string, unknown>) ?? audit;
                const auditId = String(audit.id ?? audit.key ?? '');
                const auditName = String(audit.Name__c ?? audit.name ?? data.Name__c ?? data.name ?? audit.label ?? '');
                const auditStatus = String(data.Phase_Status__c ?? data.phaseStatus ?? data.Status__c ?? data.status ?? '');
                const auditType = String(audit.auditTypeDisplayName ?? data.Audit_Type__c ?? data.auditType ?? '');
                const clientRef = String(
                  audit.Account__c ?? audit.custom__account ?? audit.ClientId__c ??
                  data.Account__c ?? data.custom__account ?? data.ClientId__c ?? ''
                );
                const clientName = lookupAccountDisplayName(accountNameMap, clientRef) || clientRef;
                const dueDate = String(data.Due_Date__c ?? data.dueDate ?? data.Due_Date ?? '');
                const auditorRef = String(data.Auditor__c ?? data.auditor ?? data.custom__auditor ?? audit.Auditor__c ?? '');
                const auditorName = lookupAuditorDisplayName(auditorNameMap, auditorRef) || '';
                const isAssigned = auditorRef && auditorRef.trim() !== '';

                return (
                  <button
                    key={auditId}
                    onClick={() => handleSelect(audit)}
                    className={`w-full p-4 text-left rounded-lg border transition-colors ${
                      isAssigned
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                        : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:border-purple-300 dark:hover:border-purple-700'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Audit Name & ID */}
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                            {auditName || `Audit ${auditId}`}
                          </span>
                          {auditStatus && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 rounded-full">
                              {auditStatus}
                            </span>
                          )}
                          {isAssigned && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300 rounded-full">
                              Assigned
                            </span>
                          )}
                        </div>

                        {/* Details Row */}
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-400">
                          {/* Client */}
                          <span className="flex items-center gap-1">
                            <Building2 className="h-3.5 w-3.5" />
                            {clientName || 'Unknown Client'}
                          </span>

                          {/* Audit Type */}
                          {auditType && (
                            <span className="flex items-center gap-1">
                              <FileText className="h-3.5 w-3.5" />
                              {auditType}
                            </span>
                          )}

                          {/* Due Date (formatted as MM/DD/YYYY) */}
                          {dueDate && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3.5 w-3.5" />
                              {formatDateForDisplay(dueDate)}
                            </span>
                          )}

                          {/* Auditor (show current assignment) */}
                          {auditorName && (
                            <span className="flex items-center gap-1 text-orange-600 dark:text-orange-400">
                              <User className="h-3.5 w-3.5" />
                              {auditorName}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
            <span>{filteredAudits.length} shown</span>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <span className="text-green-600 dark:text-green-400">{auditCounts.unassigned} unassigned</span>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <span className="text-blue-600 dark:text-blue-400">{auditCounts.assigned} assigned</span>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <span className="text-gray-400 dark:text-gray-500">{auditCounts.completed} completed</span>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuditPickerModal;
